/**
 * Portfolio API balance mapping utilities.
 *
 * Handles request/response transformations between the SDK's IGetBalancesRequest/IGetBalancesResponse
 * and the @blockdaemon-internal/portfolio-ts-client types.
 *
 * @internal
 */
import { AssetType } from "../modules/common/types";
/**
 * Converts an SDK balance request to Portfolio API parameters.
 *
 * Expects `chainId` to already be in CAIP-2 format (e.g. `"eip155:1"`).
 * Callers must normalise the chain ID before invoking this function.
 */
export function mapRequestToPortfolio(request) {
    return {
        address: request.address,
        chainIDs: request.chainId ? [request.chainId] : undefined,
        fiatType: request.fiatType,
    };
}
// ---------------------------------------------------------------------------
// Response mapping: BalancesResponse -> IGetBalancesResponse
// ---------------------------------------------------------------------------
/**
 * Resolves per-token price in the given fiat from the portfolio SDK's {@link TokenAmount}.
 *
 * When the API returns a non-USD fiat (e.g. EUR via fiatType), values are in
 * `fiatAmounts[ fiatKey ]` and `token.prices[ fiatKey ]` may be absent; we compute
 * price from fiatAmounts / tokenUnits.
 *
 * Priority:
 *  1. `token.prices[ fiatKey ]`
 *  2. Computed from `fiatAmounts[ fiatKey ] / (amount / 10^decimals)`
 */
function resolveFiatPrice(tb, fiatKey) {
    const token = tb.token;
    if (token.prices?.[fiatKey])
        return token.prices[fiatKey];
    const fiatAmount = tb.fiatAmounts?.[fiatKey] ?? (fiatKey === "USD" ? tb.amountFiat : undefined);
    if (!fiatAmount)
        return "0";
    const rawAmount = Number(tb.amount);
    const decimals = token.decimals ?? 0;
    const tokenUnits = rawAmount / 10 ** decimals;
    if (tokenUnits <= 0)
        return "0";
    return (Number(fiatAmount) / tokenUnits).toString();
}
function resolveAssetType(tags) {
    if (tags?.includes("native"))
        return AssetType.FUNGIBLE_TOKEN;
    return AssetType.ERC20;
}
/**
 * Derives all fiat currency keys from the portfolio balance response.
 * When the API returns multiple fiats (e.g. fiatType: ["JPY", "EUR"]), response.fiatAmounts has each key.
 */
function getFiatKeysFromResponse(response) {
    const keys = response.fiatAmounts && Object.keys(response.fiatAmounts);
    if (keys?.length)
        return keys;
    return ["USD"];
}
export function mapPortfolioResponseToBalances(response, chainIDs) {
    const fiatKeys = getFiatKeysFromResponse(response);
    const allBalances = response.balances ?? [];
    const chainBalances = chainIDs?.length
        ? allBalances.filter((b) => chainIDs.includes(b.chainID))
        : allBalances;
    let nativeBalance = "0";
    const tokens = [];
    for (const chainBalance of chainBalances) {
        if (!chainBalance?.tokenBalances)
            continue;
        for (const tb of chainBalance.tokenBalances) {
            const isNative = tb.token.tags?.includes("native") ?? false;
            if (isNative) {
                nativeBalance = tb.amount;
                continue;
            }
            const prices = {};
            for (const key of fiatKeys) {
                prices[key] = resolveFiatPrice(tb, key);
            }
            const fiatAmounts = {};
            for (const key of fiatKeys) {
                const amount = tb.fiatAmounts?.[key];
                if (amount != null)
                    fiatAmounts[key] = amount;
            }
            tokens.push({
                name: tb.token.name || "",
                symbol: tb.token.symbol || "",
                decimals: (tb.token.decimals ?? 0).toString(),
                address: tb.token.address || "",
                balance: tb.amount || "0",
                assetType: resolveAssetType(tb.token.tags),
                prices,
                ...(Object.keys(fiatAmounts).length > 0 && { fiatAmounts }),
                logoUrl: tb.token.logoURI || null,
                tags: tb.token.tags || [],
            });
        }
    }
    return {
        data: {
            native: nativeBalance,
            tokens,
        },
    };
}
//# sourceMappingURL=data:application/json;base64,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